import React, { useEffect, useState } from 'react'
import { Outlet, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { APP_VERSION } from '@/version'
import { Menu, X, ArrowLeft, UtensilsCrossed, BookOpen, ShoppingCart, Store } from 'lucide-react'
import { SubscriptionBanner } from '@/components/subscription/SubscriptionBanner'
import { AccountMenu } from '@/components/AccountMenu'
import { KidAccountMenu } from '@/components/KidAccountMenu'
import { SetupWizard } from '@/components/SetupWizard'
import { apiClient } from '@/utils/api'
import { isSharedSessionActive } from '@/utils/sharedDevice'
import toast from 'react-hot-toast'

export const DashboardLayout: React.FC = () => {
  const { user, logout, loginFromToken } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false)
  const [showSetupWizard, setShowSetupWizard] = useState(false)
  const [setupChecked, setSetupChecked] = useState(false)

  // OAuth token handling - check URL directly to avoid React Router timing issues
  // Use window.location.search as primary source since it's always immediately available
  const urlSearchParams = new URLSearchParams(window.location.search)
  const urlToken = urlSearchParams.get('token') || urlSearchParams.get('auth_token')
  const isGoogleLogin = urlSearchParams.get('google_login') === 'true'

  // CRITICAL: If there's a token in URL, we MUST block rendering until auth completes
  // Clear any stale sessionStorage flags that might interfere
  const [isAuthenticating, setIsAuthenticating] = useState(() => {
    if (urlToken) {
      // Clear stale flags from previous attempts
      sessionStorage.removeItem('oauth_token_processed')
      sessionStorage.removeItem('current_processing_token')
      return true
    }
    return false
  })

  // Get current page and tab info
  const isMealsPage = location.pathname === '/dashboard/meals'
  const currentTab = searchParams.get('tab') || 'meals'

  const handleMealsTabChange = (tab: string) => {
    setSearchParams({ tab })
  }

  // Handle OAuth token from URL
  useEffect(() => {
    if (!urlToken) {
      return
    }

    // Already processing check - use a ref-like approach with sessionStorage
    const currentToken = sessionStorage.getItem('current_processing_token')
    if (currentToken === urlToken) {
      // Already being processed by this render
      return
    }

    console.log('[DashboardLayout] Processing OAuth token')

    // Mark as processing
    sessionStorage.setItem('current_processing_token', urlToken)

    // Clear URL immediately to prevent re-processing on refresh
    window.history.replaceState({}, document.title, location.pathname)

    const handleOAuthLogin = async () => {
      try {
        const result = await loginFromToken(urlToken)

        if (result.success) {
          toast.success(`Welcome back, ${result.user.display_name}!`)
          sessionStorage.removeItem('current_processing_token')
        } else {
          console.error('[DashboardLayout] OAuth login failed:', result.error)
          toast.error(result.error || 'Authentication failed')
          sessionStorage.removeItem('current_processing_token')
          navigate('/login', { replace: true })
        }
      } catch (error) {
        console.error('[DashboardLayout] OAuth error:', error)
        toast.error('Authentication failed')
        sessionStorage.removeItem('current_processing_token')
        navigate('/login', { replace: true })
      } finally {
        setIsAuthenticating(false)
      }
    }

    handleOAuthLogin()
  }, [urlToken, loginFromToken, navigate, location.pathname])

  // Check setup status for new users
  useEffect(() => {
    // Only check for parents who are logged in
    if (!user || !user.is_parent || isAuthenticating || setupChecked) return
    // Never show first-run setup on a shared family device.
    if (isSharedSessionActive()) {
      setSetupChecked(true)
      return
    }

    const checkSetupStatus = async () => {
      try {
        // Check if user dismissed the wizard before
        const dismissed = localStorage.getItem('setup_wizard_dismissed')
        if (dismissed) {
          setSetupChecked(true)
          return
        }

        // Wait for token to be fully synchronized after OAuth
        // This prevents 401 race conditions during login
        const token = localStorage.getItem('auth_token')
        if (!token) {
          console.log('[DashboardLayout] No token yet, skipping setup check')
          setSetupChecked(true)
          return
        }

        const status = await apiClient.getSetupStatus()
        setSetupChecked(true)

        // Show wizard if setup not completed and user hasn't done basic setup
        if (!status.setup_completed && (status.is_first_login || !status.steps.has_kids)) {
          setShowSetupWizard(true)
        }
      } catch (error: any) {
        // Don't log 401 errors as they're expected during OAuth race conditions
        if (error?.response?.status !== 401) {
          console.error('Failed to check setup status:', error)
        }
        setSetupChecked(true)
      }
    }

    checkSetupStatus()
  }, [user, isAuthenticating, setupChecked])

  const handleSetupWizardClose = () => {
    setShowSetupWizard(false)
    // Don't show again this session
    localStorage.setItem('setup_wizard_dismissed', 'true')
  }

  const handleSetupComplete = () => {
    setShowSetupWizard(false)
    localStorage.setItem('setup_wizard_dismissed', 'true')
    toast.success('Setup complete! Welcome to HousePoints.')
  }

  // Redirect to appropriate default view based on user type
  useEffect(() => {
    // Don't redirect while authenticating
    if (isAuthenticating) return

    if (location.pathname === '/dashboard' || location.pathname === '/dashboard/') {
      // Both parents and kids go to home (which renders the appropriate dashboard)
      if (user) {
        navigate('/dashboard/home', { replace: true })
      }
    }
  }, [location.pathname, user, navigate, isAuthenticating])

  const handleLogout = () => {
    sessionStorage.removeItem('oauth_token_processed')
    logout()
    navigate('/login')
  }

  // Show loading state while authenticating OAuth token
  if (isAuthenticating) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Authenticating...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Setup Wizard for new users */}
      {showSetupWizard && !isSharedSessionActive() && (
        <SetupWizard
          onClose={handleSetupWizardClose}
          onComplete={handleSetupComplete}
        />
      )}

      {/* Subscription Warning Banner */}
      <SubscriptionBanner />

      {/* Desktop Header for parent subpages (not on home/tiles dashboard) */}
      {user?.is_parent && location.pathname !== '/dashboard/home' && (
        <div className="hidden md:block sticky top-0 z-30 bg-white border-b border-gray-200 shadow-sm">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between h-14">
              {/* Back button */}
              <button
                onClick={() => navigate('/dashboard/home')}
                className="flex items-center gap-2 px-3 py-2 -ml-3 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="font-medium">Dashboard</span>
              </button>

              {/* Contextual tabs for Meals page */}
              {isMealsPage && (
                <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg">
                  <button
                    onClick={() => handleMealsTabChange('meals')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      currentTab === 'meals'
                        ? 'bg-white text-orange-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <UtensilsCrossed className="w-4 h-4" />
                    Meal Planning
                  </button>
                  <button
                    onClick={() => handleMealsTabChange('ordering')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      currentTab === 'ordering'
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <Store className="w-4 h-4" />
                    Food Orders
                  </button>
                  <button
                    onClick={() => handleMealsTabChange('recipes')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      currentTab === 'recipes'
                        ? 'bg-white text-pink-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <BookOpen className="w-4 h-4" />
                    Recipes
                  </button>
                  <button
                    onClick={() => handleMealsTabChange('shopping')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      currentTab === 'shopping'
                        ? 'bg-white text-emerald-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <ShoppingCart className="w-4 h-4" />
                    Shopping
                  </button>
                </div>
              )}

              <AccountMenu />
            </div>
          </div>
        </div>
      )}

      {/* Header - hidden on /dashboard/home only for parents (kids see header everywhere) */}
      {(location.pathname !== '/dashboard/home' || !user?.is_parent) && (
        <div className={`sticky top-0 z-30 bg-white shadow-sm ${user?.is_parent ? 'md:hidden' : ''}`}>
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center space-x-3">
              {/* Back button - hidden on home page */}
              {location.pathname !== '/dashboard/home' && (
                <button
                  onClick={() => navigate('/dashboard/home')}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  aria-label="Back to home"
                >
                  <ArrowLeft className="w-6 h-6" />
                </button>
              )}
              <div>
                <h1 className="text-lg font-bold text-gray-900">
                  {user?.display_name}
                </h1>
                {!user?.is_parent && (
                  <div className="flex items-center space-x-3 text-sm">
                    <div className="flex items-center gap-1">
                      <span className="font-bold" style={{ color: user?.color_theme }}>
                        {(user?.available_points ?? user?.total_points ?? 0).toLocaleString()}
                      </span>
                      <span className="text-gray-500">available</span>
                    </div>
                    <div className="flex items-center gap-1 text-gray-400">
                      <span className="font-medium">{(user?.lifetime_points_earned ?? 0).toLocaleString()}</span>
                      <span>lifetime</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {user?.is_parent ? (
              <AccountMenu />
            ) : (
              <KidAccountMenu />
            )}
          </div>

          {/* Mobile tabs for Meals page */}
          {isMealsPage && user?.is_parent && (
            <div className="px-4 pb-3 flex gap-2 overflow-x-auto">
              <button
                onClick={() => handleMealsTabChange('meals')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  currentTab === 'meals'
                    ? 'bg-orange-100 text-orange-700'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                <UtensilsCrossed className="w-4 h-4" />
                Meals
              </button>
              <button
                onClick={() => handleMealsTabChange('ordering')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  currentTab === 'ordering'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                <Store className="w-4 h-4" />
                Orders
              </button>
              <button
                onClick={() => handleMealsTabChange('recipes')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  currentTab === 'recipes'
                    ? 'bg-pink-100 text-pink-700'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                <BookOpen className="w-4 h-4" />
                Recipes
              </button>
              <button
                onClick={() => handleMealsTabChange('shopping')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  currentTab === 'shopping'
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                <ShoppingCart className="w-4 h-4" />
                Shopping
              </button>
            </div>
          )}
        </div>
      )}

      {/* Content Area */}
      <div className="p-4 lg:px-4 lg:max-w-7xl lg:mx-auto">
        <Outlet />
      </div>

      {/* Footer with Version - Both Desktop and Mobile */}
      <div className="text-center text-xs text-gray-400 pb-4 mt-8">
        <p className="hidden lg:block text-sm text-gray-500 mb-2">
          HousePoints • Earn points, complete tasks, have fun!
        </p>
        <p>Version {APP_VERSION}</p>
      </div>
    </div>
  )
}
